import { resolvePackageCharge } from "@/lib/daycare-hotel/packageCharge";
import type { HotelPriceTier } from "@/lib/daycare-hotel/hotelPriceTier";
import {
  getBillingCycle,
  resolveFirstDueOn,
  type RecurringUsageRule,
  type StayPlanKind,
  type StayPlanModule,
} from "@/lib/daycare-hotel/stayPlans";

export interface StayPlanContractDraft {
  module: StayPlanModule;
  planId: string;
  startsOn: string;
  billingTiming?: "start" | "end";
  /** Compatibilidade com drafts antigos; novos contratos usam a data de início como âncora. */
  billingDay?: number;
  /** Dias fixos da matrícula (0=domingo…6=sábado). Só para creche `per_week`. */
  fixedWeekdays?: number[];
  /** Datas escolhidas na compra do pacote. Vazio = pacote sem datas ainda. */
  packageDates?: string[];
  /** Faixa de preço congelada na venda. Só hotel diferencia. */
  priceTier?: HotelPriceTier;
  payNow: boolean;
}

export interface StayPlanContractPlanInfo {
  name: string;
  price: number;
  kind: StayPlanKind;
  includedDays: number | null;
  usageRule: RecurringUsageRule | null;
  usageLimit: number | null;
  /** Só creche semanal. 2 = a cada 15 dias. */
  weekInterval?: 1 | 2;
}

export interface ContractInsert {
  clinic_id: string;
  pet_id: string;
  owner_id: string | null;
  plan_id: string;
  terms_snapshot: {
    name: string;
    price: number;
    included_days: number | null;
    usage_rule: string | null;
    usage_limit: number | null;
    /** Congelado na venda: mudar o plano depois não muda o ritmo do contrato. */
    week_interval?: number;
    /** Faixa congelada na venda. Cancelar o plano depois não recobra o pacote. */
    price_tier?: string;
  };
  starts_on: string;
  first_due_on: string;
  next_due_on: string;
  billing_timing: "start" | "end";
  remaining_days: number | null;
  carryover_units: number;
  status: "active";
}

export function buildContractInsert({
  draft,
  plan,
  clinicId,
  petId,
  ownerId,
  inheritedCarryover,
}: {
  draft: StayPlanContractDraft;
  plan: StayPlanContractPlanInfo;
  clinicId: string;
  petId: string;
  ownerId: string | null;
  inheritedCarryover: number;
}): ContractInsert {
  const isRecurring = plan.kind === "recurring";
  // `first_due_on` continua sendo a âncora do ciclo. O vencimento efetivo
  // (início/fim) é calculado na charge, sem deslocar os dias contratados.
  const firstDueOn = resolveFirstDueOn(draft.startsOn, draft.billingDay ?? Number(draft.startsOn.slice(8, 10)));
  const firstChargePeriod = buildChargePeriod({
    kind: plan.kind,
    startsOn: draft.startsOn,
    firstDueOn,
    billingTiming: draft.billingTiming,
  });
  const firstChargeDueOn = draft.billingTiming === "end" ? firstChargePeriod.periodEndsOn : firstChargePeriod.periodStartsOn;
  // Cota per_week é validada pelo uso real (getWeeklyQuotaStatus); não
  // acumula saldo de um contrato pro outro.
  const carryoverUnits = plan.usageRule === "per_week" ? 0 : inheritedCarryover;

  return {
    clinic_id: clinicId,
    pet_id: petId,
    owner_id: ownerId,
    plan_id: draft.planId,
    terms_snapshot: {
      name: plan.name,
      price: plan.price,
      included_days: plan.includedDays,
      usage_rule: plan.usageRule,
      usage_limit: plan.usageLimit,
      ...(plan.usageRule === "per_week" && plan.weekInterval === 2 ? { week_interval: 2 } : {}),
      ...(draft.priceTier ? { price_tier: draft.priceTier } : {}),
    },
    starts_on: draft.startsOn,
    first_due_on: firstDueOn,
    next_due_on: firstChargeDueOn,
    billing_timing: draft.billingTiming ?? "start",
    // Saldo cheio: quem debita é o check-in.
    remaining_days: isRecurring ? null : plan.includedDays,
    carryover_units: carryoverUnits,
    status: "active",
  };
}

export function buildChargePeriod({
  kind,
  startsOn,
  firstDueOn,
  billingTiming = "start",
  packageDates = [],
}: {
  kind: StayPlanKind;
  startsOn: string;
  firstDueOn: string;
  billingTiming?: "start" | "end" | null;
  packageDates?: string[];
}): { periodStartsOn: string; periodEndsOn: string; dueOn: string } {
  // Pacote não tem ciclo: quem manda são as datas compradas.
  if (kind !== "recurring") {
    // Sem nenhuma data, o pós-pago venceria no próprio dia da compra e o pacote
    // nasceria em atraso — por isso o fim do primeiro ciclo mensal continua
    // valendo como prazo nesse caso. A UI exige ao menos uma data para cobrar no
    // final; este ramo cobre quem chama fora dela.
    if (packageDates.length === 0 && billingTiming === "end") {
      const grace = getBillingCycle(firstDueOn, firstDueOn).end;
      return { periodStartsOn: startsOn, periodEndsOn: grace, dueOn: grace };
    }
    return resolvePackageCharge({
      billingTiming: billingTiming === "end" ? "end" : "start",
      purchasedOn: startsOn,
      dates: packageDates,
    });
  }
  const cycle = getBillingCycle(firstDueOn, firstDueOn);
  return {
    periodStartsOn: cycle.start,
    periodEndsOn: cycle.end,
    dueOn: billingTiming === "end" ? cycle.end : cycle.start,
  };
}
